import React, { useState, useContext, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router';
import { useToggle } from 'react-use';
import {
  Button,
  Card,
  Container,
  Flex,
  Grid,
  Loadable,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from '../../design-system';
import { Form } from 'react-final-form';
import ContainerHeader from '../../components/ContainerHeader';
import { successRefetch, useData } from '../../helpers/data-loader';
import { checkPermissions, errorMsg, prettyPhone } from '../../helpers/utils';
import {
  CONTACT_FIELDS,
  EDIT_CONTACT_INFO,
  STORE_CONTACT_INFO,
} from './constants';
import FieldColumn from '../../components/FieldColumn';
import { Session } from '../../contexts/sessionContext';
import { ERR_MSG, PERMISSIONS } from '../../globalConstants';
import ReadOnlyColumn from '../../components/ReadOnlyColumn';
import CancelButton from '../../components/CancelButton';
import ContactEmails from './ContactEmails';
import ModalForm from '../../components/ModalForm';
import ContactPhone from './ContactPhone';
import Alert from '../../helpers/alert';

const ContactEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);

  const canEdit = useMemo(
    () => checkPermissions(permissions, [PERMISSIONS.UPDATE_CONTACT]),
    [permissions],
  );

  const [isEditing, setEditingStatus] = useState(false);
  const [isModalOpen, toggleModal] = useToggle(false);
  const [isEditModalOpen, toggleEditModal] = useToggle(false);
  const [modalType, setModalType] = useState<any>(undefined);
  const [isDeleteModalOpen, toggleDeleteModal] = useToggle(false);

  const [contact, fetchContact, contactLoading] = useData(`/contact/${id}`, {
    errorMsg: errorMsg('Contact'),
    initialLoading: true,
    initialData: { project_memberships: [], emails: [], phones: [] },
  });

  useEffect(() => {
    fetchContact();
  }, []);

  const handleOnSubmit = async (values: any) => {
    try {
      await axios.put(`/contact/${contact.id}`, values);
      setEditingStatus(false);
      successRefetch(fetchContact);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleAddForm = async (values: any) => {
    const payload = { ...values, contact_id: contact.id };

    try {
      await axios.post(`/contact/${contact.id}/createDetails`, payload);
      toggleModal(false);
      successRefetch(fetchContact);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleEditForm = async (values: any) => {
    try {
      await axios.post(`/contact/${values.id}/updateDetails`, values);
      toggleEditModal(false);
      successRefetch(fetchContact);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleDeleteContact = async () => {
    try {
      await axios.delete(`/contact/${contact.id}`);
      toggleDeleteModal(false);
      navigate('/contacts');
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const toggleEditingStatus = () => {
    setEditingStatus(!isEditing);
  };

  const handleDeleteDetailsHandler = async (values: any) => {
    try {
      await axios.post(`/contact/${contact.id}/deleteDetails`, values);
      successRefetch(fetchContact);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  return (
    <Loadable loading={contactLoading}>
      <Container>
        <Card mt={30}>
          <ContainerHeader>
            <Text id="card-title" fontWeight={700} fontSize={25}>
              {contact.full_name}
            </Text>
          </ContainerHeader>
        </Card>
        <Card>
          <Form
            initialValues={{
              ...contact,
              phone: contact.phone && prettyPhone(contact.phone),
            }}
            onSubmit={handleOnSubmit}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Grid.Row>
                  {CONTACT_FIELDS.map((field) => {
                    if (canEdit) {
                      if (!isEditing) {
                        return (
                          <Grid.Column
                            key={field.name}
                            size={{ _: 12, md: 6, lg: 4 }}
                          >
                            <ReadOnlyColumn data={contact} field={field} />
                          </Grid.Column>
                        );
                      }

                      return (
                        <Grid.Column
                          key={field.name}
                          size={{ _: 12, md: 6, lg: 4 }}
                        >
                          <FieldColumn {...field} />
                        </Grid.Column>
                      );
                    }
                    return (
                      <Grid.Column
                        key={field.name}
                        size={{ _: 12, md: 6, lg: 4 }}
                      >
                        <ReadOnlyColumn data={contact} field={field} />
                      </Grid.Column>
                    );
                  })}
                </Grid.Row>
                <Flex mt={30} justifyContent="flex-end">
                  {canEdit &&
                    (isEditing ? (
                      <Button.Group>
                        <CancelButton
                          id="cancel-edit-button"
                          title="Cancel"
                          aria-label="Cancel"
                          type="button"
                          variant="primaryText"
                          onClick={toggleEditingStatus}
                        >
                          Cancel
                        </CancelButton>
                        <Button
                          id="exit-edit-button"
                          title="Update"
                          aria-label="Update Contact"
                          type="submit"
                        >
                          Update
                        </Button>
                      </Button.Group>
                    ) : (
                      <Button.Group>
                        <Button
                          id="enter-edit-button"
                          title="edit"
                          aria-label="Edit Contact"
                          type="button"
                          onClick={toggleEditingStatus}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="light"
                          id="delete-contact-button"
                          style={{ marginLeft: 20 }}
                          title="delete"
                          aria-label="Delete Contact"
                          type="button"
                          onClick={toggleDeleteModal}
                        >
                          Delete
                        </Button>
                      </Button.Group>
                    ))}
                </Flex>
              </form>
            )}
          />
        </Card>
        <ContactEmails
          emails={contact.emails}
          canEdit={canEdit}
          toggleModal={toggleModal}
          setModalType={setModalType}
          toggleEditModal={toggleEditModal}
          handleDeleteDetailsHandler={handleDeleteDetailsHandler}
        />
        <ContactPhone
          phones={contact.phones}
          canEdit={canEdit}
          toggleModal={toggleModal}
          setModalType={setModalType}
          toggleEditModal={toggleEditModal}
          handleDeleteDetailsHandler={handleDeleteDetailsHandler}
        />
        <ModalForm
          title="Add Contact Info"
          isOpen={isModalOpen}
          onSubmit={handleAddForm}
          onClose={toggleModal}
          fields={STORE_CONTACT_INFO}
          oneColumn
          initialValues={modalType}
        />
        <ModalForm
          title="Edit Contact Info"
          isOpen={isEditModalOpen}
          onSubmit={handleEditForm}
          onClose={toggleEditModal}
          fields={EDIT_CONTACT_INFO}
          oneColumn
          initialValues={modalType}
        />
        <Modal show={isDeleteModalOpen} onClose={toggleDeleteModal} size="md">
          <ModalHeader>Delete Contact</ModalHeader>
          <ModalContent>
            Are you sure you want to delete this contact? All of the contacts
            emails and phone numbers will also be deleted.
          </ModalContent>
          <ModalFooter>
            <Flex justifyContent="space-between">
              <Button
                variant="light"
                style={{ marginRight: 20 }}
                id="cancel-delete-button"
                title="cancel"
                aria-label="Cancel"
                type="button"
                onClick={toggleDeleteModal}
              >
                Cancel
              </Button>
              <Button
                id="actually-delete-button"
                title="Delete"
                aria-label="Delete Contact"
                type="button"
                onClick={handleDeleteContact}
              >
                Delete
              </Button>
            </Flex>
          </ModalFooter>
        </Modal>
      </Container>
    </Loadable>
  );
};

export default ContactEdit;
